import { useState } from "react";
// import { useUsersQuery } from "@/api/queries/UserQueries";
import { useDeleteUserMutation } from "@/api/mutations/UserMutations";
import { useBanUserMutation, useUnbanUserMutation } from "@/api/mutations/UserBanMutations";
// import { usePendingInvitationsQuery } from "@/api/queries/admin/InvitationQueries";
import { useUsersAndInvitationsQuery } from "@/api/queries/UserQueries";
import { useCancelInvitationMutation } from "@/api/mutations/admin/InvitationMutations";
import { DataTableSkeleton } from "@/components/data-tables/data-table-skeleton";
import UserManagementDataTable from "@/components/data-tables/user-management/UserManagementDataTable";
import UserManagementDialog from "@/components/dialogs/UserManagementDialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTranslation } from "react-i18next";

const UserManagementSection = () => {
    const { t } = useTranslation();
    const [userDialog, setUserDialog] = useState<{
        open: boolean;
        user: any;
        mode?: "create" | "edit";
    }>({ open: false, user: null, mode: "create" });
    const [deleteDialog, setDeleteDialog] = useState<{
        open: boolean;
        user: any;
    }>({ open: false, user: null });
    const [banDialog, setBanDialog] = useState<{
        open: boolean;
        user: any;
        isBanned: boolean;
    }>({ open: false, user: null, isBanned: false });
    const [banReason, setBanReason] = useState("");

    // Use the unified query that handles union and pagination on the backend
    const { data: unifiedData, isLoading } = useUsersAndInvitationsQuery({ page: "1", per_page: "100" });
    const deleteUserMutation = useDeleteUserMutation([]);
    const cancelInvitationMutation = useCancelInvitationMutation();
    const banUserMutation = useBanUserMutation();
    const unbanUserMutation = useUnbanUserMutation();

    // cast to any to resolve transient type issues while types are regenerating
    const combinedData = (unifiedData?.data || []) as any[];

    const userActions = {
        onViewDetails: (user: any) => {
            setUserDialog({ open: true, user, mode: "edit" });
        },
        onEdit: (user: any) => {
            setUserDialog({ open: true, user, mode: "edit" });
        },
        onToggleEnabled: (user: any) => {
            setBanDialog({ open: true, user, isBanned: user.banned || false });
            setBanReason("");
        },
        onDuplicate: (user: any) => {
            console.log("Duplicate user:", user.name);
            // TODO: Implement duplicate user
        },
        onDelete: (user: any) => {
            setDeleteDialog({ open: true, user });
        },
    };

    const handleDeleteConfirm = () => {
        if (deleteDialog.user) {
            // Check if it's an invitation or a regular user
            if (deleteDialog.user.isInvitation && deleteDialog.user.invitationId) {
                // Cancel the invitation
                cancelInvitationMutation.mutate({ id: deleteDialog.user.invitationId });
            } else {
                // Delete the user
                deleteUserMutation.mutate({ ids: [deleteDialog.user.id] });
            }
            setDeleteDialog({ open: false, user: null });
        }
    };

    const handleBanConfirm = () => {
        if (banDialog.user) {
            if (banDialog.isBanned) {
                // Unban user
                unbanUserMutation.mutate({ userId: banDialog.user.id });
            } else {
                // Ban user
                banUserMutation.mutate({
                    userId: banDialog.user.id,
                    banReason: banReason || t("admin.userManagement.accountDisabledByAdmin"),
                });
            }
            setBanDialog({ open: false, user: null, isBanned: false });
        }
    };

    return (
        <section className="flex flex-col gap-4">
            <div className="space-y-2">
                <h1 className="text-2xl font-semibold">{t("admin.userManagement.title")}</h1>
                <p className="text-gray-500">{t("admin.userManagement.description")}</p>
            </div>

            {isLoading ? (
                <DataTableSkeleton
                    rowCount={10}
                    columnCount={6}
                    searchableColumnCount={2}
                    filterableColumnCount={1}
                    extraButtonsCount={2}
                />
            ) : (
                <UserManagementDataTable data={combinedData} actions={userActions} />
            )}

            <UserManagementDialog
                open={userDialog.open}
                onOpenChange={(open) => setUserDialog((prev) => ({ ...prev, open }))}
                user={userDialog.user}
                mode={userDialog.mode}
            />

            {/* Delete/Cancel Confirmation Dialog */}
            <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, user: null })}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {deleteDialog.user?.isInvitation
                                ? t("admin.userManagement.cancelInvitationConfirm")
                                : t("admin.userManagement.deleteConfirm")}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {deleteDialog.user?.isInvitation
                                ? t("admin.userManagement.cancelInvitationWarning", { email: deleteDialog.user?.email })
                                : t("admin.userManagement.deleteWarning", { name: deleteDialog.user?.name })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDeleteConfirm}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {deleteDialog.user?.isInvitation
                                ? t("admin.userManagement.cancelInvitation")
                                : t("common.delete")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* Ban/Unban Confirmation Dialog */}
            <AlertDialog
                open={banDialog.open}
                onOpenChange={(open) => setBanDialog({ open, user: null, isBanned: false })}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {banDialog.isBanned
                                ? t("admin.userManagement.enableAccount")
                                : t("admin.userManagement.disableAccount")}
                        </AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-4">
                                {banDialog.isBanned ? (
                                    <p>
                                        {t("admin.userManagement.enableAccountWarning", { name: banDialog.user?.name })}
                                    </p>
                                ) : (
                                    <>
                                        <p>
                                            {t("admin.userManagement.disableAccountWarning", {
                                                name: banDialog.user?.name,
                                            })}
                                        </p>
                                        <div className="space-y-2">
                                            <Label htmlFor="ban-reason">
                                                {t("admin.userManagement.reasonForDisabling")}
                                            </Label>
                                            <Input
                                                id="ban-reason"
                                                value={banReason}
                                                onChange={(e) => setBanReason(e.target.value)}
                                                placeholder={t("admin.userManagement.enterReason")}
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                        <AlertDialogAction onClick={handleBanConfirm}>
                            {banDialog.isBanned
                                ? t("admin.userManagement.enableAccount")
                                : t("admin.userManagement.disableAccount")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </section>
    );
};

export default UserManagementSection;
